import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import { hasRole } from '../../utils/permissions';
import { normalizePermissionOverrides } from '../../utils/effectivePermissions';
import { canUserAccessReport } from '../../constants/reportAccess';
import { hasSubscriptionFeature } from '../../constants/subscriptionPlans';
import { isSubscriptionEnforcementEnabled } from '../../config/features';
import { getRoleHomePath } from '../../config/roles';
import { WORKSPACE_QUICK_LINKS } from '../../config/navigation';
import { useWorkdayGate } from '../../workday/WorkdayGateContext';
import { isWorkdayExemptPath, WORKDAY_PATHS, WORKDAY_STEPS } from '../../workday/constants';

/**
 * ProtectedRoute — auth, RBAC, subscription, and mandatory workday gate.
 */
const ProtectedRoute = ({ children, requiredRole, requiredPermission, requireBusinessId = true, requiredFeature, requiredReportKey }) => {
    const { isAuthenticated, user, business, company, distributor, loading, can, token } = useAuth();
    const location = useLocation();
    const { gated, blocked, step, loading: gateLoading, gate } = useWorkdayGate();

    const getFirstAccessiblePath = React.useCallback(() => {
        if (!user) return '/login';
        if (user.role === 'SaaSAdmin') return '/admin/dashboard';

        const roleHome = getRoleHomePath(user.role);
        const homeModule = roleHome.includes('/field/') ? null : 'dashboard';
        if (!homeModule || can('dashboard', 'view')) {
            const workspaceLinks = {
                Salesman: WORKSPACE_QUICK_LINKS.field,
                AccountsStaff: WORKSPACE_QUICK_LINKS.operations,
                Accountant: WORKSPACE_QUICK_LINKS.operations,
                WarehouseStaff: WORKSPACE_QUICK_LINKS.warehouse,
            }[user.role];
            if (workspaceLinks) {
                const first = workspaceLinks.find((l) => {
                    if (!l.permission) return true;
                    const [mod, act] = l.permission.split(':');
                    return can(mod, act);
                });
                if (first) return roleHome;
            }
        }

        const candidates = [
            { path: roleHome, module: null },
            { path: '/dashboard', module: 'dashboard', action: 'view' },
            { path: '/drafts', module: 'drafts', action: 'view' },
            { path: '/draft-invoices', module: 'drafts', action: 'view' },
            { path: '/draft-receipts', module: 'drafts', action: 'view' },
            { path: '/customer-invoices', module: 'invoices', action: 'view' },
            { path: '/payment-receipts', module: 'paymentsReceipts', action: 'view' },
            { path: '/customers', module: 'customers', action: 'view' },
            { path: '/inventory/products', module: 'products', action: 'view' },
            { path: '/reports', module: 'reports', action: 'view' },
        ];

        const firstAllowed = candidates.find((c) => !c.module || can(c.module, c.action));
        return firstAllowed?.path || '/login';
    }, [can, user]);

    // Don't block the whole app on workday gate when offline — show shell with last-known gate
    const waitingOnGate = isAuthenticated && gated && gateLoading && !gate
        && (typeof navigator === 'undefined' || navigator.onLine);
    // Stay on spinner while session hydrates (token without user) to avoid login bounce on new tabs
    const hydratingSession = Boolean(token && !user);
    if (loading || waitingOnGate || hydratingSession) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                backgroundColor="#D2EFE6"
            >
                <CircularProgress sx={{ color: '#3A8667' }} />
            </Box>
        );
    }

    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace />;
    }

    if (user.role === 'SaaSAdmin' && !requireBusinessId) {
        return children;
    }

    if (requireBusinessId && user.role !== 'SaaSAdmin') {
        const hasBusinessContext = business || company || distributor;
        if (!hasBusinessContext) {
            const hierarchyRoles = [
                'Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin',
                'Salesman', 'WarehouseStaff', 'AccountsStaff', 'Accountant',
                'ZSM', 'ASM', 'RSM', 'NSM', 'HRManager',
            ];
            if (!hierarchyRoles.includes(user.role)) {
                return <Navigate to="/login" replace />;
            }
        }
    }

    // Mandatory workday validation — block all modules until cleared
    if (gated && blocked && !isWorkdayExemptPath(location.pathname)) {
        const target = gate?.redirectPath
            || WORKDAY_PATHS[step]
            || WORKDAY_PATHS[WORKDAY_STEPS.PENDING_CHECKIN];
        if (location.pathname !== target) {
            return <Navigate to={target} replace />;
        }
    }

    const denyAccess = () => {
        const target = getFirstAccessiblePath();
        if (target === location.pathname) {
            return <Navigate to="/unauthorized" replace />;
        }
        return <Navigate to={target} replace />;
    };

    const [permResource, permAction] = requiredPermission ? requiredPermission.split(':') : [];

    // The role list stays in force, but an admin who explicitly granted this exact
    // module/action to this user can widen access beyond the user's role.
    if (requiredRole) {
        const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        const overrides = normalizePermissionOverrides(user?.permissionOverrides);
        const grantedByAdmin = !!(permResource && permAction && overrides?.[permResource]?.[permAction] === true);
        if (!hasRole(user.role, allowedRoles) && !grantedByAdmin) {
            return denyAccess();
        }
    }

    if (requiredPermission) {
        if (!permResource || !permAction || !can(permResource, permAction)) {
            return denyAccess();
        }
    }

    if (requiredReportKey && !canUserAccessReport(user, requiredReportKey)) {
        return denyAccess();
    }

    if (requiredFeature && user.role !== 'SaaSAdmin' && isSubscriptionEnforcementEnabled()) {
        const planName = business?.subscriptionPlan;
        if (planName && !hasSubscriptionFeature(planName, requiredFeature)) {
            return <Navigate to="/subscription/pricing" replace state={{ from: location.pathname, requiredFeature }} />;
        }
    }

    return children;
};

export default ProtectedRoute;
